/**
 * MIN-316 finding 4 — the chat's readable measure, defined once.
 *
 * The transcript and the composer are the same column: a turn's text must start
 * where the box the clinician types into starts, and stop where it stops. Before
 * this, neither had a constraint at all — `Conversation` shipped no `max-width`
 * anywhere, so `apps/ai` rendered a 1,406px transcript over its own 736px
 * composer and `apps/provider` let both grow with the window (roughly 200
 * characters a line at a 1,512px viewport).
 *
 * Two deliberate choices here:
 *
 * 1. **One constant, not a repeated utility class.** The cap belongs to the
 *    package, so a consumer composing `Thread` + `PromptInput` gets a readable
 *    column without wrapping either in a width of its own — and a future
 *    consumer cannot land a transcript that disagrees with its composer.
 *
 * 2. **Resolved style, not a Tailwind class.** A `max-width` that silently
 *    fails to apply is exactly the defect being fixed, and a class name is not
 *    observable in a unit test (happy-dom performs no layout, and the utility
 *    is not compiled there at all). As inline style the constraint is asserted
 *    directly, and no Tailwind build step stands between the constant and the
 *    element.
 *
 * `maxWidth` caps without setting a width, so below the cap both boxes still
 * fill what they are given — a narrow window, and `apps/provider`'s gutter
 * panel, are unaffected.
 */
import type { CSSProperties } from "react";

/** The column cap — Tailwind's `3xl` (48rem/768px), the width both apps'
 * composers were already wrapped to before this became shared. */
export const CHAT_MEASURE_MAX_WIDTH = "48rem";

/** The gutter inside the measure, so the transcript's text and the composer's
 * own border sit on the same two vertical lines. */
export const CHAT_MEASURE_PADDING_INLINE = "1rem";

/** Longhands rather than the `margin-inline`/`padding-inline` shorthands: the
 * per-side values are what a caller (and a test) reads back. */
export const chatMeasureStyle: CSSProperties = {
  width: "100%",
  maxWidth: CHAT_MEASURE_MAX_WIDTH,
  marginLeft: "auto",
  marginRight: "auto",
  paddingLeft: CHAT_MEASURE_PADDING_INLINE,
  paddingRight: CHAT_MEASURE_PADDING_INLINE,
};
